import { useState, useMemo } from "react";
import { NumberField, Stat, ToolShell } from "./parts";
import { inr } from "@/lib/format";
import { Input } from "@/components/ui/input";

const CII = {
  "2001-02": 100, "2010-11": 167, "2012-13": 200, "2014-15": 240, "2016-17": 264,
  "2017-18": 272, "2018-19": 280, "2019-20": 289, "2020-21": 301, "2021-22": 317,
  "2022-23": 331, "2023-24": 348, "2024-25": 363,
};

// Finance Act 2024 cut-off: assets acquired BEFORE 23 Jul 2024 retain the choice
// between 20% with indexation and 12.5% without indexation (pick lower).
// Assets acquired on/after this date are taxed only at 12.5% (no indexation).
const CUTOFF = "2024-07-23";

export function CapitalGains() {
  const [purchase, setPurchase] = useState(4000000);
  const [sale, setSale] = useState(7000000);
  const [holding, setHolding] = useState(36);
  const [buyCII, setBuyCII] = useState(254);
  const [sellCII, setSellCII] = useState(363);
  const [purchaseDate, setPurchaseDate] = useState("2019-04-15");

  const result = useMemo(() => {
    const longTerm = holding >= 24;
    const gainNoIndex = sale - purchase;
    if (!longTerm) {
      return { type: "Short-Term", note: "Taxed at your income slab rate.", options: [["STCG (added to income)", gainNoIndex, null]] };
    }
    const preCutoff = !!purchaseDate && purchaseDate < CUTOFF;
    const indexedCost = purchase * (sellCII / buyCII);
    const gainIndexed = Math.max(0, sale - indexedCost);
    const taxIndexed = gainIndexed * 0.20;
    const taxNoIndex = Math.max(0, gainNoIndex) * 0.125;

    if (preCutoff) {
      return {
        type: "Long-Term",
        note: "Acquired before 23 Jul 2024 — Finance Act 2024 lets you pick the lower of the two.",
        indexedCost,
        options: [
          ["With Indexation @ 20%", gainIndexed, taxIndexed],
          ["Without Indexation @ 12.5%", Math.max(0, gainNoIndex), taxNoIndex],
        ],
        best: Math.min(taxIndexed, taxNoIndex),
      };
    }
    return {
      type: "Long-Term",
      note: "Acquired on/after 23 Jul 2024 — flat 12.5% without indexation (Finance Act 2024).",
      options: [
        ["Without Indexation @ 12.5%", Math.max(0, gainNoIndex), taxNoIndex],
      ],
      best: taxNoIndex,
    };
  }, [purchase, sale, holding, buyCII, sellCII, purchaseDate]);

  const preCutoff = purchaseDate && purchaseDate < CUTOFF;

  return (
    <ToolShell title="Capital Gains" desc="Estimate tax on selling your property. Post Finance Act 2024, only properties acquired before 23 Jul 2024 retain the choice between 20% with indexation and 12.5% without.">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="space-y-8">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Property Purchase Date</label>
            <Input
              type="date"
              value={purchaseDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="tool-input"
              data-testid="cg-purchase-date"
            />
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-cyan">
              {preCutoff ? "Pre-cut-off · Both regimes available" : "Post-cut-off · Only 12.5% no-index applies"}
            </p>
          </div>
          <NumberField label="Purchase Price" value={purchase} onChange={setPurchase} min={100000} max={200000000} step={100000} prefix="₹" testid="cg-purchase" />
          <NumberField label="Sale Price" value={sale} onChange={setSale} min={100000} max={300000000} step={100000} prefix="₹" testid="cg-sale" />
          <NumberField label="Holding Period" value={holding} onChange={setHolding} min={1} max={240} step={1} suffix="mo" testid="cg-holding" />
          {preCutoff && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <NumberField label="CII (Buy Year)" value={buyCII} onChange={setBuyCII} min={100} max={363} step={1} testid="cg-buycii" />
                <NumberField label="CII (Sell Year)" value={sellCII} onChange={setSellCII} min={100} max={400} step={1} testid="cg-sellcii" />
              </div>
              <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
                CII (Cost Inflation Index) reference: 2016-17=264 · 2020-21=301 · 2024-25=363
              </p>
            </>
          )}
        </div>
        <div className="space-y-5">
          <Stat label="Gain Type" value={result.type} testid="cg-type" />
          {result.options.map(([label, gain, tax]) => (
            <div key={label} className="border border-border bg-background p-5" data-testid="cg-option">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
              <div className="mt-2 flex items-end justify-between">
                <span className="font-mono text-sm text-muted-foreground">Gain {inr(gain)}</span>
                {tax !== null && <span className="font-mono text-xl font-bold text-foreground">Tax {inr(tax)}</span>}
              </div>
            </div>
          ))}
          {result.best !== undefined && (
            <Stat label="Best-case Tax Payable" value={inr(result.best)} highlight testid="cg-result" />
          )}
          <p className="font-mono text-[10px] text-muted-foreground">{result.note} This is an estimate — consult a CA for filing.</p>
        </div>
      </div>
    </ToolShell>
  );
}
